import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ActivityMap } from '../../logic/activity';
import { dayKeyOf } from '../../logic/activity';
import { IconNavButton } from '../touchable/IconNavButton';
import { DayCell } from './DayCell';

interface MonthGridProps {
  viewYear: number;
  viewMonth: number; // 0-indexed (matches Date.getMonth)
  activity: ActivityMap;
  patternColors: Map<string, string>; // patternId -> resolved hex
  today: Date;
  earliestKey: string | null;
  selectedKey: string;
  onSelectDay: (key: string) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Monday-first weekday labels
const WEEKDAY_LABELS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

/** Returns the 0-indexed offset (0=Mo, 6=Su) for the first day of the month. */
function mondayOffset(date: Date): number {
  // Date.getDay: 0=Su, 1=Mo, ..., 6=Sa. We want 0=Mo, 6=Su.
  return (date.getDay() + 6) % 7;
}

export const MonthGrid: React.FC<MonthGridProps> = ({
  viewYear,
  viewMonth,
  activity,
  patternColors,
  today,
  earliestKey,
  selectedKey,
  onSelectDay,
  onPrevMonth,
  onNextMonth,
}) => {
  const firstOfMonth = new Date(viewYear, viewMonth, 1);
  const leadingBlanks = mondayOffset(firstOfMonth);
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const todayKey = dayKeyOf(today);

  const earliestMonth = earliestKey
    ? earliestKey.slice(0, 7) // "YYYY-MM"
    : null;
  const viewMonthKey = `${viewYear.toString().padStart(4, '0')}-${(viewMonth + 1).toString().padStart(2, '0')}`;
  const todayMonthKey = `${today.getFullYear().toString().padStart(4, '0')}-${(today.getMonth() + 1).toString().padStart(2, '0')}`;

  const prevDisabled = earliestMonth !== null && viewMonthKey <= earliestMonth;
  const nextDisabled = viewMonthKey >= todayMonthKey;

  const cells: React.ReactNode[] = [];
  for (let i = 0; i < leadingBlanks; i++) {
    cells.push(<div key={`blank-${i}`} className="aspect-square" />);
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const key = `${viewMonthKey}-${d.toString().padStart(2, '0')}`;
    const day = activity.get(key);
    const totalMs = day?.totalMs ?? 0;
    const dominantId = day?.dominantPatternId ?? null;
    const dominantColor = dominantId ? patternColors.get(dominantId) ?? null : null;
    const markers: { kind: 'start' | 'finish'; color: string }[] = [];
    if (day) {
      for (const ev of day.starts) {
        markers.push({ kind: 'start', color: patternColors.get(ev.patternId) ?? '#999' });
      }
      for (const ev of day.finishes) {
        markers.push({ kind: 'finish', color: patternColors.get(ev.patternId) ?? '#999' });
      }
    }
    cells.push(
      <DayCell
        key={key}
        dayNumber={d}
        totalMs={totalMs}
        dominantColor={dominantColor}
        markers={markers}
        isToday={key === todayKey}
        isSelected={key === selectedKey}
        onSelect={() => onSelectDay(key)}
      />,
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <IconNavButton
          aria-label="Previous month"
          disabled={prevDisabled}
          onClick={onPrevMonth}
        >
          <ChevronLeft className="w-5 h-5" />
        </IconNavButton>
        <h2 className="text-lg font-bold text-ink">
          {MONTH_NAMES[viewMonth]} {viewYear}
        </h2>
        <IconNavButton
          aria-label="Next month"
          disabled={nextDisabled}
          onClick={onNextMonth}
        >
          <ChevronRight className="w-5 h-5" />
        </IconNavButton>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold uppercase tracking-widest text-ink-faint mb-1">
        {WEEKDAY_LABELS.map((w) => (
          <div key={w}>{w}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells}
      </div>
    </div>
  );
};
